import { useState } from 'react';

import { isObjectiveHolderRemovable, removeObjectiveHolder } from '@/utils/objectiveHolders';

interface UseOwnerHandoverOptions {
    /** The objective's employees, owner first. */
    selectedEmployeeIds: string[];
    onChange: (employeeIds: string[]) => void;
    /** Someone who only manages their own objectives neither hands one over nor takes one. */
    isOwnerLocked: boolean;
    /** Whom the server lets own an objective for this reader. */
    isWithinReach: (employeeId: string) => boolean;
}

/**
 * Changes an objective's employees under the owner rules of `removeObjectiveHolder`, and remembers
 * a refused handover until the employees change again, so the field can say why the owner stayed.
 */
export function useOwnerHandover({
    selectedEmployeeIds,
    onChange,
    isOwnerLocked,
    isWithinReach,
}: UseOwnerHandoverOptions) {
    const [isOwnerKept, setIsOwnerKept] = useState(false);

    const changeEmployees = (employeeIds: string[]) => {
        setIsOwnerKept(false);
        onChange(employeeIds);
    };

    const removeEmployee = (employeeId: string) => {
        const remaining = removeObjectiveHolder(selectedEmployeeIds, employeeId, isWithinReach);
        if (remaining) changeEmployees(remaining);
        else setIsOwnerKept(true);
    };

    const isRemovable = (employeeId: string, index: number) =>
        isObjectiveHolderRemovable({ holderId: employeeId, index, isOwnerLocked, isWithinReach });

    return { isOwnerKept, changeEmployees, removeEmployee, isRemovable };
}
